// client/src/pages/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const CATEGORY_COLORS = {
  'Clinical Research': '#dbeafe', 'Drug Development': '#dcfce7',
  'Patient Advocacy': '#fce7f3', 'Regulatory': '#fef9c3',
  'Public Health': '#e0f2fe', 'Technology': '#ede9fe', 'News': '#fee2e2',
};
const CATEGORY_TEXT = {
  'Clinical Research': '#1d4ed8', 'Drug Development': '#15803d',
  'Patient Advocacy': '#be185d', 'Regulatory': '#854d0e',
  'Public Health': '#0369a1', 'Technology': '#7c3aed', 'News': '#b91c1c',
};

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/blog/${slug}`)
      .then(({ data }) => {
        setPost(data.blog);
        // Fetch related posts from same category
        return axios.get('/api/blog', { params: { category: data.blog.category, limit: 3 } });
      })
      .then(({ data }) => {
        setRelated((data.blogs || []).filter(b => b.slug !== slug).slice(0, 3));
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spin" style={{ width: 32, height: 32, border: '2px solid var(--line-card)', borderTopColor: 'var(--ink)', borderRadius: '50%' }} />
    </div>
  );

  if (notFound) return (
    <div style={{ textAlign: 'center', padding: '100px 24px' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 12, color: 'var(--ink)' }}>Article Not Found</div>
      <button onClick={() => navigate('/blog')} style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '10px 24px', borderRadius: 0, cursor: 'pointer', fontSize: 14 }}>← Back to Blog</button>
    </div>
  );

  if (!post) return null;

  return (
    <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' }}>
      {/* Article header */}
      <div style={{ background: 'var(--paper)', color: 'var(--ink)', padding: '52px 24px 48px', borderBottom: '2px solid var(--ink)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/blog')}
            style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 24, padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            ← BACK TO JOURNAL
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '3px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              {post.category}
            </span>
            <span style={{ color: 'var(--muted-2)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{post.readTime} min read</span>
            <span style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{formatDate(post.publishedAt)}</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4.5vw, 46px)', lineHeight: 1.1, marginBottom: 20, color: 'var(--ink)' }}>
            {post.title}
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 16, lineHeight: 1.6, maxWidth: 680 }}>{post.excerpt}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--line)' }}>
            <div style={{ width: 36, height: 36, background: 'var(--ink)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: 'var(--paper)', fontWeight: 700, fontFamily: 'var(--font-display)' }}>
              {(post.author || 'T')[0]}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{post.author || 'BioMedArticles Editorial'}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>BioMedArticles Journal</div>
            </div>
            {post.tags?.slice(0, 3).map(tag => (
              <span key={tag} style={{ marginLeft: 'auto', background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '3px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>#{tag}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Optional image */}
      {post.image && (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ marginTop: -20, borderRadius: 0, overflow: 'hidden', border: '1px solid var(--line-card)', boxShadow: '6px 6px 0 rgba(10,10,10,0.06)', maxHeight: 480 }}>
            <img src={post.image} alt={post.imageAlt || post.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        </div>
      )}

      {/* Article content + sidebar */}
      <div className="blog-layout" style={{ maxWidth: 1160, margin: '0 auto', padding: '52px 24px', display: 'grid', gridTemplateColumns: '1fr 300px', gap: 60, alignItems: 'start' }}>
        {/* Main content */}
        <article>
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
            style={{
              fontSize: 16, lineHeight: 1.8, color: 'var(--ink-2)',
              fontFamily: 'var(--font-body)'
            }}
          />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--line)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 10 }}>Tags</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {post.tags.map(tag => (
                  <span key={tag} style={{ background: 'var(--paper-2)', color: 'var(--muted)', fontSize: 12, padding: '4px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>#{tag}</span>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Sidebar */}
        <aside style={{ position: 'sticky', top: 80 }}>
          {/* CTA */}
          <div style={{ background: 'var(--ink)', color: 'var(--paper)', borderRadius: 0, padding: '24px 22px', marginBottom: 24 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 8, lineHeight: 1.3 }}>Search Clinical Trials</div>
            <p style={{ color: 'var(--muted-2)', fontSize: 13, marginBottom: 18, lineHeight: 1.6 }}>Browse 574,000+ studies from ClinicalTrials.gov — free access.</p>
            <button onClick={() => navigate('/')} style={{ width: '100%', background: 'var(--paper)', color: 'var(--ink)', border: 'none', padding: '11px', fontSize: 13, fontWeight: 700, borderRadius: 0, cursor: 'pointer' }}>
              Search Trials →
            </button>
          </div>

          {/* Related posts */}
          {related.length > 0 && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '20px 22px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 16 }}>Related Articles</div>
              {related.map((r, i) => (
                <div key={r._id}
                  onClick={() => navigate(`/blog/${r.slug}`)}
                  style={{ paddingBottom: i < related.length - 1 ? 14 : 0, marginBottom: i < related.length - 1 ? 14 : 0, borderBottom: i < related.length - 1 ? '1px solid var(--line-card)' : 'none', cursor: 'pointer' }}
                >
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 400, lineHeight: 1.35, color: 'var(--ink)', marginBottom: 4 }}
                    onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={e => e.target.style.textDecoration = 'none'}
                  >{r.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>{r.readTime} min · {formatDate(r.publishedAt)}</div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {/* Blog content styles */}
      <style>{`
        .blog-content h2 { font-family: var(--font-display); font-size: 26px; color: var(--ink); margin: 40px 0 16px; line-height: 1.3; }
        .blog-content h3 { font-family: var(--font-display); font-size: 20px; color: var(--ink-2); margin: 32px 0 12px; line-height: 1.4; }
        .blog-content p { margin-bottom: 20px; }
        .blog-content ul, .blog-content ol { padding-left: 24px; margin-bottom: 20px; }
        .blog-content li { margin-bottom: 8px; line-height: 1.7; }
        .blog-content strong { color: var(--ink); font-weight: 700; }
        .blog-content a { color: var(--ink); text-decoration: underline; text-underline-offset: 3px; }
        .blog-content a:hover { color: var(--muted); }
        .blog-content blockquote { border-left: 3px solid var(--ink); padding: 12px 20px; margin: 24px 0; background: var(--paper-2); font-style: italic; color: var(--muted); }
        @media (max-width: 768px) {
          .blog-content { font-size: 15px; }
          .blog-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}